// /onboarding — signup completion for new Google (OAuth) users.
//
// Google sign-in auto-creates the account, so these users land WITHOUT
// having accepted the terms or entered a company. The /app guard
// (routes/app/route.tsx) bounces `needs_onboarding` users here. We collect
// name + company + TOS consent (and optional marketing açık rıza), POST to
// identity-service /v1/auth/complete-onboarding, then drop them into /app.
// v0.3.15 (2026-06-29).

import { useState, type FormEvent } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { MeResponse } from "@/lib/auth";
import { Icons } from "@/components/icons";
import { AuthCanvas, HeroPane } from "./sign-up";

export const Route = createFileRoute("/onboarding")({
  beforeLoad: async ({ context }) => {
    let me: MeResponse;
    try {
      me = await context.queryClient.ensureQueryData({
        queryKey: ["me"],
        queryFn: () => api<MeResponse>("/auth/me"),
        staleTime: 60_000,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: "/sign-in", search: { from: "/onboarding" } });
      }
      throw err;
    }
    // Already completed → no reason to be here.
    if (!me?.needs_onboarding) {
      throw redirect({ to: "/app" });
    }
  },
  component: OnboardingPage,
});

function OnboardingPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const qc = useQueryClient();
  const me = qc.getQueryData<MeResponse>(["me"]);

  const [displayName, setDisplayName] = useState(me?.user.display_name ?? "");
  const [company, setCompany] = useState("");
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const formValid =
    displayName.trim().length >= 2 && company.trim().length >= 1 && acceptedTos;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!formValid || busy) return;
    setBusy(true);
    try {
      await api("/auth/complete-onboarding", {
        method: "POST",
        body: {
          display_name: displayName.trim(),
          company_name: company.trim(),
          accept_tos: acceptedTos,
          consent_marketing: acceptedMarketing,
        },
      });
      // v0.1.x — clear the gate locally, then HARD-navigate into the app.
      // Previously we awaited invalidateQueries(["me"]) + a router nav; that
      // refetch could hang / race, leaving the button stuck on "Kaydediliyor…"
      // even though onboarding succeeded server-side (onboarded=true). A full
      // page load re-fetches /me cleanly (now needs_onboarding=false) so the
      // /app guard passes without any stale-cache redirect loop.
      qc.setQueryData<MeResponse>(["me"], (old) =>
        old ? { ...old, needs_onboarding: false } : old,
      );
      window.location.assign("/app");
      return; // keep the button in its saving state until the reload takes over
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError
          ? e.message
          : tr
            ? "Bir hata oluştu. Lütfen tekrar dene."
            : "Something went wrong. Please try again.",
      );
      setBusy(false);
    }
  }

  return (
    <AuthCanvas>
      <HeroPane tr={tr} />

      <div className="card" style={{ padding: 32, background: "var(--surface)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Son adım" : "Last step"}</div>
        <h2 style={{ fontSize: 24, marginBottom: 8 }}>{tr ? "Kaydını tamamla." : "Complete your signup."}</h2>
        <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 22, lineHeight: 1.5 }}>
          {tr
            ? "Devam etmek için birkaç bilgiye ihtiyacımız var."
            : "We just need a couple of details to continue."}
        </p>

        <form onSubmit={onSubmit}>
          <label className="mono" style={{ fontSize: 11, color: "var(--mu)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {tr ? "Ad Soyad" : "Full name"}
          </label>
          <input
            className="input"
            type="text"
            autoComplete="name"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={tr ? "Ad Soyad" : "Full name"}
            style={{ margin: "6px 0 14px" }}
          />

          <label className="mono" style={{ fontSize: 11, color: "var(--mu)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {tr ? "Şirket adı" : "Company name"}
          </label>
          <input
            className="input"
            type="text"
            autoComplete="organization"
            required
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder={tr ? "Firmanızın adı" : "Your company"}
            style={{ margin: "6px 0 16px" }}
          />

          {/* TOS + Privacy + KVKK — bundled, required (same as sign-up) */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "var(--tx2)", marginBottom: 10, lineHeight: 1.5 }}>
            <input type="checkbox" required checked={acceptedTos} onChange={(e) => setAcceptedTos(e.target.checked)} style={{ marginTop: 3, accentColor: "var(--accent)" }} />
            <span>
              {tr ? (
                <>
                  <a href="https://nexusdijital.com/kullanim-sartlari" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Kullanım Şartları</a>
                  {"'nı, "}
                  <a href="https://nexusdijital.com/gizlilik" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Gizlilik Politikası</a>
                  {"'nı ve "}
                  <a href="https://nexusdijital.com/aydinlatma-metni" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>KVKK Aydınlatma Metni</a>
                  {"'ni okudum, kabul ediyorum."}
                </>
              ) : (
                <>
                  I have read and accept the{" "}
                  <a href="https://nexusdijital.com/kullanim-sartlari" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Terms of Service</a>
                  ,{" "}
                  <a href="https://nexusdijital.com/gizlilik" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Privacy Policy</a>
                  {" and "}
                  <a href="https://nexusdijital.com/aydinlatma-metni" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>KVKK Disclosure</a>
                  .
                </>
              )}
            </span>
          </label>

          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "var(--tx2)", marginBottom: 18, lineHeight: 1.5 }}>
            <input type="checkbox" checked={acceptedMarketing} onChange={(e) => setAcceptedMarketing(e.target.checked)} style={{ marginTop: 3, accentColor: "var(--accent)" }} />
            <span>
              {tr
                ? <>Yeni özellik ve kampanyalardan haberdar olmak istiyorum <span style={{ color: "var(--mu)" }}>(opsiyonel · KVKK açık rıza)</span>.</>
                : <>Send me product updates and announcements <span style={{ color: "var(--mu)" }}>(optional)</span>.</>}
            </span>
          </label>

          {err ? <div role="alert" style={{ color: "var(--error)", fontSize: 12, marginBottom: 14 }}>{err}</div> : null}

          <button type="submit" disabled={!formValid || busy} className="btn btn-primary" style={{ width: "100%", justifyContent: "center", padding: "11px 16px", opacity: !formValid || busy ? 0.6 : 1 }}>
            {busy ? (tr ? "Kaydediliyor…" : "Saving…") : (tr ? "Devam et" : "Continue")}
            <Icons.ArrowRight size={14} />
          </button>
        </form>
      </div>
    </AuthCanvas>
  );
}
